// frontend/src/hooks/photoInboxCache.ts
/**
 * Changes to a tank's cached inbox that fetch as little as possible. The grid
 * is an infinite query, and invalidating one fetches every page it has loaded
 * again, one after another; these fetch one page at most. No React here: the
 * upload queue uses them too.
 */

import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { tanksQueryKey } from '@/api/tanks';
import type { PhotoInboxItem, PhotoInboxPage } from '@/types/photoInbox';
import { photoInboxKeys } from './photoInboxKeys';
import { tankPhotoKeys } from './tankPhotoKeys';

type InboxPages = InfiniteData<PhotoInboxPage, string | null>;

/**
 * The grid's order, as the server pages it: capture time, then id. The time
 * is compared to the microsecond, which the server sends ("...05.123456Z", or
 * no fraction at all) and Date.parse drops.
 */
function compareOrder(a: PhotoInboxItem, b: PhotoInboxItem): number {
  const micros = (iso: string): number => {
    const fraction = /\.(\d+)/.exec(iso)?.[1] ?? '';
    return (
      Date.parse(iso.replace(/\.\d+/, '')) * 1000 + Number(fraction.padEnd(6, '0').slice(0, 6))
    );
  };
  return micros(a.taken_at) - micros(b.taken_at) || a.id - b.id;
}

/**
 * Puts a photo back into the cached pages (an undone assign or discard), in
 * its place. One that belongs after the pages loaded so far is left out: it
 * comes with a later page.
 */
export function insertIntoPages(
  queryClient: QueryClient,
  tankId: string,
  photo: PhotoInboxItem
): void {
  queryClient.setQueryData<InboxPages>(photoInboxKeys.list(tankId), (data) => {
    if (!data || data.pages.some((page) => page.items.some((item) => item.id === photo.id))) {
      return data;
    }
    const pages = [...data.pages];
    for (let index = 0; index < pages.length; index++) {
      const page = pages[index] as PhotoInboxPage;
      const at = page.items.findIndex((item) => compareOrder(item, photo) > 0);
      if (at >= 0) {
        pages[index] = {
          ...page,
          items: [...page.items.slice(0, at), photo, ...page.items.slice(at)],
        };
        return { ...data, pages };
      }
    }
    const last = pages[pages.length - 1];
    if (!last || last.next_after !== null) return data;
    pages[pages.length - 1] = { ...last, items: [...last.items, photo] };
    return { ...data, pages };
  });
}

/**
 * After a batch of uploads: the grid starts over from its first page (the
 * only one fetched, and only while the grid is shown) and the count is
 * fetched. Cached photos whose neighbours may have changed are fetched again
 * when next shown.
 */
export function refreshInboxAfterUploads(queryClient: QueryClient, tankId: string): void {
  void queryClient.resetQueries({ queryKey: photoInboxKeys.list(tankId), exact: true });
  void queryClient.invalidateQueries({ queryKey: photoInboxKeys.count(tankId), exact: true });
  void queryClient.invalidateQueries({
    queryKey: [...photoInboxKeys.tank(tankId), 'item'],
    refetchType: 'none',
  });
}

/**
 * Photos added to Tank Shots by an upload batch (tank shots design 6.5): the
 * Tank Shots page and the tanks list (covers, "Tank shots · N"). The inbox is
 * left alone, so its count (the nav badge) doesn't flicker.
 */
export function refreshTankShots(queryClient: QueryClient, tankId: string): void {
  void queryClient.invalidateQueries({ queryKey: tankPhotoKeys.all(tankId) });
  void queryClient.invalidateQueries({ queryKey: tanksQueryKey });
}
